<template>
  <div>
    <v-toolbar flat class="rounded-b-0 mr-8" style="background: transparent;">
      <v-spacer />

      <template v-if="currentHighlight">
        <v-tooltip bottom>
          <template #activator="{ on }">
            <span v-on="on" @click="setHighlight(null)" class="text-center">
              <v-icon :color="currentHighlight.type === tidSelection ? 'petrolPastel' : 'mostardPastel'">
                fiber_manual_record
              </v-icon>
              <a class="white--text">
                {{ `${currentHighlight.type === tidSelection ? 'TID' : 'CID'}: ${currentHighlight.val}` }}
              </a>
            </span>
          </template>
          <span>{{ $tc('stop_tracking') }}</span>
        </v-tooltip>
      </template>

      <template v-if="!isPopup && !pinned && $vuetify.breakpoint.smAndUp">
        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-btn icon v-on="on" @click="openPopup">
              <v-icon outline>
                aspect_ratio
              </v-icon>
            </v-btn>
          </template>
          <span>{{ $tc('popup_window') }}</span>
        </v-tooltip>
      </template>

      <template v-if="isPopup || pinned">
        <v-tooltip :disabled="$vuetify.breakpoint.xsOnly" bottom>
          <template #activator="{ on }">
            <v-btn icon v-on="on" @click="cancelPinOnSide">
              <v-icon outline>
                clear
              </v-icon>
            </v-btn>
          </template>
          <span>{{ $tc('close') }}</span>
        </v-tooltip>
      </template>

      <v-tooltip :disabled="$vuetify.breakpoint.xsOnly" bottom>
        <template #activator="{ on }">
          <v-btn icon v-on="on" :disabled="items.length <= 0" @click="reloadLogs">
            <v-icon outline>
              autorenew
            </v-icon>
          </v-btn>
        </template>
        <span>{{ $tc('reload') }}</span>
      </v-tooltip>

      <template v-if="isStarted">
        <v-tooltip :disabled="$vuetify.breakpoint.xsOnly" bottom>
          <template #activator="{ on }">
            <v-btn v-on="on" icon @click="stopLogs">
              <v-icon outline>
                pause
              </v-icon>
            </v-btn>
          </template>
          <span>{{ $tc('stop') }}</span>
        </v-tooltip>
      </template>

      <template v-else>
        <v-tooltip :disabled="$vuetify.breakpoint.xsOnly" bottom>
          <template #activator="{ on }">
            <v-btn id="play_events_monitor" icon v-on="on" @click="startLogs">
              <v-icon outline>
                play_arrow
              </v-icon>
            </v-btn>
          </template>
          <span>{{ $tc('start') }}</span>
        </v-tooltip>
      </template>
    </v-toolbar>

    <v-card
      id="events_monitor"
      :class="{
        'rounded-lg': !isPopup,
        'rounded-0': isPopup,
        'h-100v': isPopup,
        'h-85v': (!isPopup && !pinned) && $vuetify.breakpoint.xsOnly,
        'h-70v': (!isPopup && !pinned) && $vuetify.breakpoint.smAndUp && !isFullPage,
        '': isFullPage
      }"
      class="mx-md-10 ml-2 mb-4"
      flat
      outlined
    >
      <template v-if="items.length <= 0 && isStarted">
        <v-col class="pr-3 pl-3">
          <v-progress-linear :indeterminate="true" />
        </v-col>
      </template>

      <template v-if="items.length <= 0 && !isStarted">
        <v-container fluid fill-height>
          <v-row align="center" justify="center">
            <v-col cols="12" sm="8" md="4">
              <v-row justify="center">
                <v-btn @click="startLogs" icon width="120" height="120">
                  <v-icon color="timid" size="120">
                    play_circle_outline
                  </v-icon>
                </v-btn>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
      </template>
      <template v-if="items.length > 0">
        <v-virtual-scroll
          :items="items"
          :item-height="104"
          bench="1"
        >
          <template #default="{ item }">
            <v-list-item
              :key="`events-${item.counter}`"
              v-if="item.date"
              class="eventItem"
              :class="getBackgroundColor(item)"
            >
              <v-list-item-avatar>
                <v-icon :color="getArrowIconColor(item)">
                  subdirectory_arrow_right
                </v-icon>
              </v-list-item-avatar>

              <v-list-item-content class="py-2">
                <v-list-item-subtitle class="my-1">
                  <a :class="getTextColorClass(item)">
                    {{ item.date }}
                  </a>
                </v-list-item-subtitle>
                <v-list-item-subtitle class="my-1" :class="getTextColorClass(item)">
                  <a :class="getTextColorClass(item)">
                    {{ item.title }}
                  </a>
                </v-list-item-subtitle>
                <v-list-item-subtitle class="my-1">
                  <v-row no-gutters>
                    <v-col cols="auto">
                      <p @click="setHighlight(item, item.tid)">
                        <a class="text-link pr-2" :class="getTextColorClass(item)">{{ item.tid }}</a>
                      </p>
                    </v-col>

                    <v-col cols="auto">
                      <p :class="getTextColorClass(item)">
                        {{ item.counter }}
                      </p>
                    </v-col>

                    <v-col cols="auto">
                      <p @click="setHighlight(item, item.cidName)">
                        <a class="text-link px-2" :class="getTextColorClass(item)">{{ item.cidName }}</a>
                      </p>
                    </v-col>
                  </v-row>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-divider v-if="item.date" :key="`events-divider-${item.counter}`" />
          </template>
        </v-virtual-scroll>
      </template>
    </v-card>
    <v-snackbar
      v-model="limitReached"
      :timeout="4000"
    >
      {{ $tc('logmon_limit_warning') }}
    </v-snackbar>
  </div>
</template>

<script>
import DrawerMixin from '@/mixins/drawer'
export default {
  mixins: [DrawerMixin],
  props: {
    isFullPage: {
      type: Boolean,
      required: false,
      default: false
    },
    pinned: {
      type: Boolean,
      required: false,
      default: () => false
    },
    height: {
      type: Number,
      default: function () {
        if (this.$vuetify.breakpoint.xsOnly) {
          const height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight
          return height
        } else {
          return 360
        }
      }
    }
  },
  data () {
    return {
      popup: null,
      tidSelection: 0,
      cidSelection: 1,
      snackbar: false
    }
  },
  computed: {
    limitReached: {
      get () {
        return this.$store.state.logMon.lineLimitReached
      },
      set (val) {
        return this.$store.commit('logMon/TOOGLE_LIMIT_REACHED')
      }
    },
    items () {
      return this.$store.state.logMon.parsedLogs
    },
    isStarted () {
      return this.$store.state.logMon.logMonReq !== null
    },
    isPopup () {
      return this.$route.name === 'monitor-events-popup'
    },
    currentHighlight () {
      return this.$store.state.logMon.currentHighlight
    },
    darkMode () {
      return this.$store.state.ui.darkModeEnabled
    }
  },
  methods: {
    getBackgroundColor (item) {
      if (!this.currentHighlight) return ''
      else {
        if (this.currentHighlight.val === item.tid) {
          return 'bg-petrol'
        } else if (this.currentHighlight.val === item.cidName) {
          return 'bg-mostard'
        }
      }
    },
    getArrowIconColor (item) {
      let isHighlighted = false
      if (this.currentHighlight) {
        isHighlighted = this.currentHighlight.val === item.tid || this.currentHighlight.val === item.cidName
      }
      return this.darkMode && isHighlighted ? 'default_icon_color' : ''
    },
    getTextColorClass (item) {
      let isHighlighted = false
      if (this.currentHighlight) {
        isHighlighted = this.currentHighlight.val === item.tid || this.currentHighlight.val === item.cidName
      }
      return this.darkMode && isHighlighted ? 'text_secondary_invert--text' : ''
    },
    setHighlight (lineObj, selectedString) {
      if (!lineObj) {
        this.$store.dispatch('logMon/setHighlight', { lineObj: null, selectionType: null })
      } else {
        const selectionType = selectedString === lineObj.tid ? this.tidSelection : this.cidSelection
        this.$store.dispatch('logMon/setHighlight', { lineObj, selectionType })
      }
    },
    startLogs () {
      this.$store.dispatch('logMon/start')
    },
    stopLogs () {
      this.$store.dispatch('logMon/stop')
    },
    reloadLogs () {
      this.$store.commit('logMon/RESET_PARSED_LOG')
      this.$store.commit('logMon/SET_LAST_LINE', null)
    },
    openPopup () {
      this.stopLogs()
      const name = 'monitor-events-popup'
      const route = this.$router.resolve({ name })

      this.$store
        .dispatch('ui/setPopup', {
          exists: this.popup,
          url: route.href
        })
        .then(res => {
          const self = this
          if (res) {
            res.addEventListener('beforeunload', () => {
              self.popup.close()
              self.popup = null
            })

            self.popup = res
          }
        })
    },
    togglePinOnSide () {
      return this.$store.commit('ui/SET_RIGHT_DRAWER', true)
    },
    pinOnSide () {
      return this.$store.commit('ui/SET_RIGHT_DRAWER', true)
    },
    cancelPinOnSide () {
      return this.$store.commit('ui/SET_RIGHT_DRAWER', false)
    }
  }
}
</script>

<style scoped>
.text-link
{
  text-decoration: underline;
}
</style>
